/**
 * Turning a picked listing into the values a document actually renders.
 *
 * The realtor may correct any of them for THIS document without touching the
 * listing record (`ListingOverride`). Addresses are edited as structured parts
 * — street / house number / apartment / floor / city — and the rendered
 * strings are derived from those parts, so "what the document says" and "what
 * the form asks for" can never drift apart.
 */

import {
  formatContractListingAddress,
  formatFullListingAddress,
  formatRedactedListingAddress
} from "../../../services/signatureFormatters";
import {
  addressPartsToFragments,
  buildAddressPartsFromListing,
  mergeAddressParts
} from "./listingAddressRequirements";
import type { ListingAddressParts } from "./listingAddressRequirements";
import type { ProcessedListingData } from "../../../@types/sharedSchema";

/**
 * Per-document, per-listing edits. String-shaped because they come straight
 * out of TextFields: an absent key means "untouched — use the listing's own
 * value", `""` means "the realtor cleared it on purpose for this document".
 * Numeric fields coerce via `Number()` and treat NaN as cleared too.
 *
 * `redactedAddress` is the one address value that ISN'T derived from the
 * parts: how much of an address to reveal before signing is a judgement call,
 * so on hidden-addresses sends the realtor can hand-write it. Untouched, it
 * falls back to the auto-redacted form of the parts.
 */
export type ListingOverride = Partial<ListingAddressParts> &
  Partial<Record<"redactedAddress" | "price" | "rooms" | "squareMeter", string>>;

/** The listing values a document renders. */
export type ListingSendContext = {
  /** The address rendered in the document body / listings table. */
  address?: string;
  /** Everything we know — powers the hidden-addresses addendum and the seller questionnaire. */
  fullAddress?: string;
  street?: string;
  houseNumber?: number;
  city?: string;
  price?: number;
  rooms?: number;
  squareMeter?: number;
};

/** `""`/NaN → undefined, so a cleared field renders as blank rather than "NaN". */
const overrideNumber = (
  raw: string | undefined,
  fallback: number | undefined
): number | undefined => {
  if (raw === undefined) return fallback;
  const parsed = Number(raw);
  return raw.trim() === "" || Number.isNaN(parsed) ? undefined : parsed;
};

/** The listing's own address parts with the realtor's edits laid on top. */
export function getEffectiveAddressParts(
  listing: ProcessedListingData,
  override?: ListingOverride
): ListingAddressParts {
  return mergeAddressParts(buildAddressPartsFromListing(listing), override);
}

/**
 * Build the values this listing contributes to the document.
 *
 * `hideAddresses` flips which address the document shows: normally the full
 * contract address (street + number, apartment, floor, city); on a hidden send
 * the redacted form, with the real one disclosed by the post-signature
 * addendum instead. On a hidden send we also drop the `street`/`houseNumber`
 * fragments so no renderer fallback can rebuild the address into the main
 * document.
 */
export function buildEffectiveListingContext(
  listing: ProcessedListingData,
  override: ListingOverride | undefined,
  hideAddresses = false
): ListingSendContext {
  const parts = getEffectiveAddressParts(listing, override);
  const fragments = addressPartsToFragments(parts, listing.address?.neighborhood);

  const fullAddress = formatFullListingAddress(fragments) || undefined;
  const redactedAddress =
    override?.redactedAddress !== undefined
      ? override.redactedAddress.trim() || undefined
      : formatRedactedListingAddress(fragments) || undefined;

  return {
    address: hideAddresses
      ? redactedAddress
      : formatContractListingAddress(fragments) || undefined,
    fullAddress,
    street: hideAddresses ? undefined : fragments.street,
    houseNumber: hideAddresses ? undefined : fragments.houseNumber,
    city: fragments.city,
    price: overrideNumber(override?.price, (listing as any).price ?? (listing as any).askingPrice),
    rooms: overrideNumber(override?.rooms, (listing as any).roomsCount ?? (listing as any).rooms),
    squareMeter: overrideNumber(override?.squareMeter, (listing as any).squareMeter)
  };
}

/**
 * The property address the owner questionnaire (שאלון מוכר / שאלון בעל זכות
 * בנכס) is prefilled with: the FULL address, else the one the document shows,
 * else street + house number — the send form's `listing.address` fallback.
 * Empty when the picked listing has none of them. The send must then STOP:
 * a send without the address used to drop the questionnaire silently while
 * the form said it was attached.
 */
export function questionnairePropertyAddress(context: ListingSendContext | undefined): string {
  if (!context) return "";
  const streetAndNumber = [context.street, context.houseNumber]
    .map((part) => (part === undefined || part === null ? "" : String(part).trim()))
    .filter(Boolean)
    .join(" ");
  return [context.fullAddress, context.address, streetAndNumber].map((value) => (value ?? "").trim()).find(Boolean) ?? "";
}

/**
 * What the signer sees BEFORE signing on a hidden-addresses send, and what the
 * addendum discloses after. Rendered as a preview under the address inputs so
 * the realtor never has to send one to find out.
 */
export function getAddressPreview(
  listing: ProcessedListingData,
  override: ListingOverride | undefined,
  hideAddresses: boolean
): { documentAddress: string; fullAddress: string } {
  const context = buildEffectiveListingContext(listing, override, hideAddresses);
  return {
    documentAddress: context.address ?? "",
    fullAddress: context.fullAddress ?? ""
  };
}
